import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
// import AsyncCreatableSelect from 'react-select/async-creatable';
import AsyncSelect from 'react-select/async';
import { useDebouncedEffect } from "../../hooks/useDebounceEffect";
import { lookupOptions } from "./hooks/useOptionsLookup";
import { useSideChannelSubscription } from "../../util/useSideChannel";
import getPathFromId from '../../util/getPathFromId';
import { useJnx } from "../../util/jnx";


function Autocomplete(props) {
    const {
        schema: {
            title,
            lookup,
            type
        },
        formContext,
        formContext: {
            setFormDataValues,
            sideChannel
        },
        value,
        formData,
        onChange,
        disabled,
        required,
        idSchema: { $id } = {},
        uiSchema: {
            'ui:readonly': uiReadonly,
            'ui:readonlyIf': uiReadonlyIf,
            'akc:requiredIfVisible': requiredIfVisible,
            'ui:onSelect': uiOnSelect,
            'ui:onChange': uiOnChange, // <-- add ui:onChange
            'ui:showIf': uiShowIf,
        },
        readonly: propReadonly
    } = props;
    const {
        resource = "Options",
        jnx: apiResponseJnxExpr,
        defaultValueExpr,
        onSelectExpr,
        params = {},
        api,
        id,
        label: labelExpr,
        searchParam,
        setObjectAs,
        allowNoValue = true,
        noValue = ''
    } = lookup || {};

    const promiseRef = useRef();


    const rootFormData = useSideChannelSubscription(sideChannel, 0);

    const path = useMemo(() => getPathFromId($id), [$id]);

    const functionBinds = useMemo(() => ({ set: setFormDataValues }), [setFormDataValues]);

    const onDefaultValue = useJnx(defaultValueExpr, {functionBinds});
    const defaultValue = useMemo(() => onDefaultValue?.eval(
        rootFormData || {}, path, {
        formData, rootFormData,
        formContext
    }
    ), [onDefaultValue, defaultValueExpr, formData, rootFormData, path]);

    const labelJnx = useJnx(labelExpr);

    const onSelectJnx = useJnx(onSelectExpr, {functionBinds});
    const label = useMemo(() => onSelectJnx?.eval(
        rootFormData || {}, path, {
        formData, rootFormData,
        formContext
    }
    ), [onSelectJnx, formData, rootFormData, path]);

    const uiShowIfJnx = useJnx(uiShowIf, {functionBinds});
    const hideElement = useMemo(() => uiShowIfJnx?.eval(
        rootFormData || {}, path, {
        formData, rootFormData,
        formContext
    }
    ), [uiShowIfJnx, formData, rootFormData, path]);

    // Add: onSelect system from LookupFormField.js, renamed to avoid conflicts
    const onSelectHandlerJnx = useJnx(lookup?.onSelect, { functionBinds });
    const onSelectHandler = useCallback(
        (option, context) => {
            if (onSelectHandlerJnx) {
                onSelectHandlerJnx.eval(
                    rootFormData || {},
                    path,
                    {
                        option,
                        context,
                        formData,
                        rootFormData,
                        formContext,
                    }
                );
            }
        },
        [onSelectHandlerJnx, rootFormData, path, formData, formContext]
    );

    const uiOnSelectJnx = useJnx(uiOnSelect, { functionBinds });
    const uiOnSelectHandler = useCallback(
        (optionObj, context) => {
            if (uiOnSelectJnx) {
                uiOnSelectJnx.eval(
                    rootFormData || {}, path, {
                        option: optionObj,
                        context,
                        formData, rootFormData,
                        formContext
                    }
                );
            }
        },
        [uiOnSelectJnx, rootFormData, path, formData, formContext]
    );

    // Add: ui:onChange system
    const uiOnChangeJnx = useJnx(uiOnChange, { functionBinds });
    const uiOnChangeHandler = useCallback(
        (optionObj, context) => {
            if (uiOnChangeJnx) {
                uiOnChangeJnx.eval(
                    rootFormData || {},
                    path,
                    {
                        option: optionObj,
                        context,
                        formData,
                        rootFormData,
                        formContext,
                    }
                );
            }
        },
        [uiOnChangeJnx, rootFormData, path, formData, formContext]
    );

    const [current, setCurrent] = useState(value);
    useDebouncedEffect(() => {
        if (current !== value){ // && current !== null && current !== undefined) {
            onChange(current.value)
        }
    }, [current], 450);
    useEffect(() => setCurrent(value), [value]);


    const readonlyIfJnx = useJnx(uiReadonlyIf);
    const readOnlyIf = useMemo(() => (
        readonlyIfJnx && readonlyIfJnx.eval(rootFormData || {}, '', {
            root: rootFormData,
            formContext,
        })
    ), [value, rootFormData, formContext]);

    const readonly = uiReadonly || propReadonly || readOnlyIf;

    const promiseOptions = useCallback((inputValue, isQR = false) => {
        const searchValue = inputValue.trim();
        if (searchValue.length > 2) {
            const search = { searchParam };
            search.promise = new Promise((resolve) => {
                search.resolve = resolve;
            });
            promiseRef.current = search.promise;
            setTimeout(async () => {
                if (promiseRef.current === search.promise) {
                    const options = await lookupOptions({ ...lookup, params: { ...lookup.params, [searchParam]: searchValue } }, rootFormData);
                    if (isQR) {
                        const obj = options?.[0];
                        setCurrent({ value: obj[id], label: labelJnx.eval(obj) });
                        search.resolve([{
                            value: obj[id],
                            label: labelJnx.eval(obj)
                        }]);
                    } else {
                        const dataParse = (options || []).map((obj) => {
                            return {
                                value: obj[id],
                                label: labelJnx.eval(obj)
                            };
                        });
                        search.resolve(dataParse);
                    }
                }
            }, 300);
            return search.promise;
        }
        return Promise.resolve([]);
    }, [rootFormData, searchParam, lookupOptions, id, labelJnx]);

    useEffect(() => {
        if (label) {
            promiseOptions(label, true);
        }
    }, [label, onSelectJnx, promiseOptions]);

    const isRequired = required || requiredIfVisible;

    // Find the full object from options when current changes, and call handlers
    useEffect(() => {
        if (!current) return;
        // Try to find the full object from the latest options
        // If you have access to the last loaded options, use them here
        // For this example, we use current as the object
        const selectedObj = current; // Replace with lookup if you have the full list
        uiOnSelectHandler(selectedObj);
        uiOnChangeHandler(selectedObj);
    }, [current]);
    // console.log("hideElement",hideElement);
    // if (hideElement) {
    //     return <div/>;
    // }
    return (
        <div className="autocomplete" data-cy={$id}>
            {title.trim() ? (<label className="control-label" >
              {title}{isRequired ? <span className="required">*</span> : null}
            </label>) : null}
            <AsyncSelect
                required={isRequired}
                isDisabled={readonly || disabled}
                cacheOptions
                defaultOptions
                value={current || defaultValue}
                loadOptions={promiseOptions}
                onChange={(option, context) => {
                    setCurrent(option);
                    onChange(option?.value);
                    // Handlers now called in useEffect after current changes
                }}
            />
        </div>
    )
}

export default Autocomplete;